import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

interface BuckyballBackgroundProps {
  quality?: 'low' | 'medium' | 'high';
}

export default function BuckyballBackground({ quality = 'high' }: BuckyballBackgroundProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const meshRef = useRef<THREE.Group | null>(null);
  const glowMeshRef = useRef<THREE.Group | null>(null);
  const particlesRef = useRef<THREE.Points | null>(null);
  const energyFieldRef = useRef<THREE.Points | null>(null);
  const frameIdRef = useRef<number>(0);
  const mousePosition = useRef({ x: 0, y: 0 });
  const qualityRef = useRef(quality);
  const devicePerformance = useRef<'low' | 'medium' | 'high'>('high');
  const isInitializedRef = useRef(false);

  // Device detection and performance assessment
  useEffect(() => {
    // Detect device capabilities
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const isOlderDevice = typeof navigator.deviceMemory !== 'undefined' ? navigator.deviceMemory < 4 : false;
    const isLowPowerDevice = isMobile && isOlderDevice;
    
    // Set performance level based on device detection
    if (isLowPowerDevice) {
      devicePerformance.current = 'low';
    } else if (isMobile) {
      devicePerformance.current = 'medium';
    } else {
      devicePerformance.current = 'high';
    }

    // Override with passed quality prop if specified
    qualityRef.current = quality || devicePerformance.current;
    
    // Log for debugging
    console.debug(`BuckyballBackground: Device performance level set to ${devicePerformance.current}, using quality ${qualityRef.current}`);
  }, [quality]);

  useEffect(() => {
    if (!containerRef.current || isInitializedRef.current) return;
    isInitializedRef.current = true;

    const getQualitySettings = () => {
      switch (qualityRef.current) {
        case 'low':
          return {
            particleCount: 500,      // Reduced particles
            energyFieldCount: 500,   // Reduced energy field particles
            nodeDetail: 1,           // Lowest detail for geometry
            enablePulse: false,      // Disable pulse animations
            enableGlow: false,       // Disable glow effects
            antialias: false,        // Disable antialiasing
            pixelRatio: 0.75         // Lower resolution
          };
        case 'medium':
          return {
            particleCount: 800,
            energyFieldCount: 1000,
            nodeDetail: 1,
            enablePulse: true,
            enableGlow: true,
            antialias: true,
            pixelRatio: 1.0
          };
        case 'high':
        default:
          return {
            particleCount: 1500,
            energyFieldCount: 2000,
            nodeDetail: 2,
            enablePulse: true,
            enableGlow: true,
            antialias: true,
            pixelRatio: Math.min(window.devicePixelRatio, 2)
          };
      }
    };

    const qualitySettings = getQualitySettings();
    
    // Enhanced scene setup with fog for depth
    const scene = new THREE.Scene();
    scene.fog = new THREE.FogExp2(0x000000, 0.05);
    sceneRef.current = scene;

    // Cinematic camera setup
    const camera = new THREE.PerspectiveCamera(
      70,
      window.innerWidth / window.innerHeight,
      0.1,
      1000
    );
    camera.position.z = 7;
    cameraRef.current = camera;

    // High-quality renderer setup adjusted for device capability
    const renderer = new THREE.WebGLRenderer({ 
      alpha: true,
      antialias: qualitySettings.antialias,
      powerPreference: "high-performance",
      stencil: false,
      depth: true
    });
    renderer.setSize(window.innerWidth, window.innerHeight);
    renderer.setClearColor(0x000000, 0);
    renderer.setPixelRatio(qualitySettings.pixelRatio);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.toneMappingExposure = 1.2;
    containerRef.current.appendChild(renderer.domElement);
    rendererRef.current = renderer;

    // Create main geometry group for complex structure
    const mainGroup = new THREE.Group();
    const glowGroup = new THREE.Group();

    // Multiple layers of icosahedrons with different sizes and rotations
    const createLayer = (radius: number, detail: number, color: number, opacity: number) => {
      const geometry = new THREE.IcosahedronGeometry(radius, qualitySettings.nodeDetail);
      const material = new THREE.MeshBasicMaterial({
        color,
        wireframe: true,
        transparent: true,
        opacity,
        blending: THREE.AdditiveBlending
      });
      return new THREE.Mesh(geometry, material);
    };

    // Create multiple layers with different properties - reduce number for low-end devices
    const layerCount = qualitySettings.enableGlow ? 4 : 2;
    const layers = [];
    
    for (let i = 0; i < layerCount; i++) {
      const baseRadius = 2.5 - (i * 0.05);
      const baseOpacity = 0.3 - (i * 0.05);
      layers.push(createLayer(baseRadius, qualitySettings.nodeDetail, 0x00a8cc, baseOpacity));
    }

    layers.forEach(layer => {
      mainGroup.add(layer);
      if (qualitySettings.enableGlow) {
        const glowLayer = layer.clone();
        glowLayer.scale.multiplyScalar(1.02);
        glowGroup.add(glowLayer);
      }
    });

    // Energy field particles - simplified for low-end devices
    const energyFieldGeometry = new THREE.BufferGeometry();
    const energyFieldCount = qualitySettings.energyFieldCount;
    const energyFieldPositions = new Float32Array(energyFieldCount * 3);
    const energyFieldSizes = new Float32Array(energyFieldCount);
    const energyFieldColors = new Float32Array(energyFieldCount * 3);

    for (let i = 0; i < energyFieldCount; i++) {
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);
      const radius = 2.5 + Math.random() * 2;

      energyFieldPositions[i * 3] = radius * Math.sin(phi) * Math.cos(theta);
      energyFieldPositions[i * 3 + 1] = radius * Math.sin(phi) * Math.sin(theta);
      energyFieldPositions[i * 3 + 2] = radius * Math.cos(phi);

      energyFieldSizes[i] = Math.random() * 2;

      // Create color gradient from teal to blue
      const color = new THREE.Color();
      color.setHSL(0.5 + Math.random() * 0.1, 0.8, 0.6);
      energyFieldColors[i * 3] = color.r;
      energyFieldColors[i * 3 + 1] = color.g;
      energyFieldColors[i * 3 + 2] = color.b;
    }

    energyFieldGeometry.setAttribute('position', new THREE.BufferAttribute(energyFieldPositions, 3));
    energyFieldGeometry.setAttribute('size', new THREE.BufferAttribute(energyFieldSizes, 1));
    energyFieldGeometry.setAttribute('color', new THREE.BufferAttribute(energyFieldColors, 3));

    const energyFieldMaterial = new THREE.PointsMaterial({
      size: 0.05,
      transparent: true,
      opacity: 0.6,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true
    });

    const energyField = new THREE.Points(energyFieldGeometry, energyFieldMaterial);
    energyFieldRef.current = energyField;
    scene.add(energyField);

    // Ambient particle system - reduced for low-end devices
    const particlesGeometry = new THREE.BufferGeometry();
    const particleCount = qualitySettings.particleCount;
    const positions = new Float32Array(particleCount * 3);
    const particleSizes = new Float32Array(particleCount);
    const colors = new Float32Array(particleCount * 3);

    for (let i = 0; i < particleCount; i++) {
      const distance = Math.random() * 10;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(Math.random() * 2 - 1);

      positions[i * 3] = distance * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = distance * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = distance * Math.cos(phi);
      
      particleSizes[i] = Math.random() * 2;

      const color = new THREE.Color();
      color.setHSL(0.5 + Math.random() * 0.2, 0.7, 0.5);
      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }

    particlesGeometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particlesGeometry.setAttribute('size', new THREE.BufferAttribute(particleSizes, 1));
    particlesGeometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const particlesMaterial = new THREE.PointsMaterial({
      size: 0.05,
      transparent: true,
      opacity: 0.4,
      vertexColors: true,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: true
    });

    const particles = new THREE.Points(particlesGeometry, particlesMaterial);
    particlesRef.current = particles;
    scene.add(particles);

    // Add meshes to scene
    scene.add(mainGroup);
    if (qualitySettings.enableGlow) {
      scene.add(glowGroup);
    }
    meshRef.current = mainGroup;
    glowMeshRef.current = glowGroup;

    // Dynamic lighting system - simplified for low-end devices
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
    scene.add(ambientLight);
    
    if (qualitySettings.enableGlow) {
      const pointLight1 = new THREE.PointLight(0x00a8cc, 1, 100);
      const pointLight2 = new THREE.PointLight(0x0088ff, 1, 100);
      pointLight1.position.set(5, 5, 5);
      pointLight2.position.set(-5, -5, -5);
      scene.add(pointLight1, pointLight2);
    }

    // Enhanced mouse interaction - throttled for better performance
    let lastMouseMoveTime = 0;
    const throttleDelay = qualityRef.current === 'low' ? 100 : (qualityRef.current === 'medium' ? 50 : 16);
    
    const handleMouseMove = (event: MouseEvent) => {
      const now = Date.now();
      if (now - lastMouseMoveTime < throttleDelay) return;
      
      lastMouseMoveTime = now;
      mousePosition.current = {
        x: (event.clientX / window.innerWidth) * 2 - 1,
        y: -(event.clientY / window.innerHeight) * 2 + 1
      };
    };

    // Responsive handling
    const handleResize = () => {
      if (!cameraRef.current || !rendererRef.current) return;

      camera.aspect = window.innerWidth / window.innerHeight;
      camera.updateProjectionMatrix();
      renderer.setSize(window.innerWidth, window.innerHeight);
      renderer.setPixelRatio(qualitySettings.pixelRatio);
    };

    // Advanced animation loop with performance optimizations
    let lastFrameTime = 0;
    const targetFrameRate = qualityRef.current === 'low' ? 30 : 60;
    const frameInterval = 1000 / targetFrameRate;
    
    const animate = (currentTime: number) => {
      frameIdRef.current = requestAnimationFrame(animate);
      
      // Throttle animation updates based on quality setting
      if (currentTime - lastFrameTime < frameInterval) {
        return;
      }
      
      lastFrameTime = currentTime;

      if (!meshRef.current || !particlesRef.current || 
          !energyFieldRef.current || !rendererRef.current || !sceneRef.current || 
          !cameraRef.current) return;

      const time = currentTime * 0.001;

      // Complex rotation patterns - simplified for low performance devices
      if (qualityRef.current !== 'low') {
        meshRef.current.rotation.x += 0.001;
        meshRef.current.rotation.y += 0.002;
        meshRef.current.rotation.z += Math.sin(time * 0.5) * 0.001;

        // Individual layer animations
        meshRef.current.children.forEach((layer, index) => {
          layer.rotation.x += 0.001 * (index + 1);
          layer.rotation.y += 0.001 * Math.sin(time * 0.2) * (index + 1);
        });
      } else {
        // Simpler animation for low-end devices
        meshRef.current.rotation.x += 0.0005;
        meshRef.current.rotation.y += 0.001;
      }
      
      if (glowMeshRef.current) {
        glowMeshRef.current.rotation.copy(meshRef.current.rotation);
      }

      // Energy field animation - optimized for performance
      energyFieldRef.current.rotation.y += 0.0003;
      
      if (qualitySettings.enablePulse) {
        const energyFieldSizes = energyFieldRef.current.geometry.attributes.size.array as Float32Array;
        for (let i = 0; i < energyFieldSizes.length; i += 2) {  // Only update half the particles for low/medium
          energyFieldSizes[i] = Math.sin(time + i) * 0.1 + 0.1;
        }
        energyFieldRef.current.geometry.attributes.size.needsUpdate = true;
      }

      // Particle system animation - simplified for lower end devices
      particlesRef.current.rotation.y += 0.0002;
      
      if (qualitySettings.enablePulse) {
        const particleSizes = particlesRef.current.geometry.attributes.size.array as Float32Array;
        const updateFrequency = qualityRef.current === 'low' ? 4 : (qualityRef.current === 'medium' ? 2 : 1);
        
        for (let i = 0; i < particleSizes.length; i += updateFrequency) {
          particleSizes[i] = Math.sin(time + i) * 0.1 + 0.1;
        }
        particlesRef.current.geometry.attributes.size.needsUpdate = true;
      }

      // Smooth mouse interaction - less sensitive on low-end devices
      const interactionFactor = qualityRef.current === 'low' ? 0.02 : (qualityRef.current === 'medium' ? 0.03 : 0.05);
      const targetRotationX = mousePosition.current.y * 0.5;
      const targetRotationY = mousePosition.current.x * 0.5;
      
      meshRef.current.rotation.x += (targetRotationX - meshRef.current.rotation.x) * interactionFactor;
      meshRef.current.rotation.y += (targetRotationY - meshRef.current.rotation.y) * interactionFactor;
      
      if (glowMeshRef.current) {
        glowMeshRef.current.rotation.copy(meshRef.current.rotation);
      }

      // Dynamic glow and pulse effects - optional based on quality setting
      if (qualitySettings.enableGlow && glowMeshRef.current) {
        const pulseScale = 1 + Math.sin(time * 2) * 0.05;
        glowMeshRef.current.scale.set(pulseScale, pulseScale, pulseScale);
      }

      // Cinematic camera movement - less movement on low-end devices
      if (qualityRef.current !== 'low') {
        camera.position.x += (mousePosition.current.x * 0.5 - camera.position.x) * 0.05;
        camera.position.y += (-mousePosition.current.y * 0.5 - camera.position.y) * 0.05;
      }
      camera.lookAt(scene.position);

      renderer.render(scene, camera);
    };

    animate(0);

    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('resize', handleResize);

    return () => {
      if (containerRef.current && rendererRef.current) {
        containerRef.current.removeChild(rendererRef.current.domElement);
      }
      
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('resize', handleResize);
      
      cancelAnimationFrame(frameIdRef.current);
      isInitializedRef.current = false;
      
      // Cleanup all geometries and materials
      [meshRef.current, glowMeshRef.current, particlesRef.current, energyFieldRef.current].forEach(obj => {
        if (obj) {
          if (obj instanceof THREE.Points) {
            obj.geometry.dispose();
            (obj.material as THREE.Material).dispose();
          } else if (obj instanceof THREE.Group) {
            obj.children.forEach(child => {
              if (child instanceof THREE.Mesh) {
                child.geometry.dispose();
                (child.material as THREE.Material).dispose();
              }
            });
          }
        }
      });
      
      rendererRef.current?.dispose();
    };
  }, []);

  return (
    <div 
      ref={containerRef} 
      className="absolute inset-0 pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
}